/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { getUserShareSettings } from "../../../../Actions/Actions";
import { setActiveApp } from "../../../../Actions/LoadScoutActions";
import {
    clearRetailMatchResults,
    getResults,
    getRetailMatchGeographies,
    setLoadingRetailMatchReport,
    setRetailMatchBenchmarkRecord,
    setSelectedBrand
} from "../../../../Actions/RetailMatchActions";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { ActiveApp } from "../../../../Enums/Enums";
import { RenderType } from "../../../../Enums/RenderType";
import { RetailMatchGeographyOption } from "../../../../Enums/RetailMatchGeographyOption";
import { IBrandOverviewTableRecord } from "../../../../Models/IBrandOverviewTableRecord";
import { IDigitalShareItem } from "../../../../Models/IDigitalShareItem";
import { IUserShareSettings } from "../../../../Models/IUserShareSettings";
import {
    getRetailMatchReportDataReady,
    loadingRetailMatchReportSelector,
    retailMatchActiveAppSelector,
    retailMatchReportDataSelector
} from "../../../../Reducers/Selectors/RetailMatchSelectors";
import { userShareSettingsSelector } from "../../../../Reducers/Selectors/UserSelectors";
import {
    GPSInsightsTemplateDisclaimer,
    retailMatchDigitalShareOutputs
} from "../../../../Util/ScoutDigitalShareUtil";
import { topNavHeightPx } from "../../../../View/Products/Constants/StyleConstants";
import {
    ComponentLoading,
    getMosaicSegments,
    ReportOuterWrapper
} from "../../../Common/";
import {
    DigitalShareGenerator,
    PrintGenerator
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import "../../../Layout/RechartsOverrides.css";
import RetailMatchDigitalShareTemplate from "../DigitalShare/Templates/RetailMatchDigitalShareTemplate";
import RetailMatchIntroTextHeader from "./RetailMatchIntroTextHeader";
import { StyledTable } from "./RetailMatchLocationsAnalyzed";
import RetailMatchMultiBrand from "./RetailMatchMultiBrand";
import RetailMatchReport from "./RetailMatchReport";

interface IRetailMatchReportContainerProps {
    isSharing?: boolean;
    jobId: string;
    brandId?: string;
    isScout: boolean;
}

export interface IRetailMatchReportContainerRouteParams {
    jobId: string;
    brandId?: string;
}

export interface IRetailMatchReportContainerShareRouteParams {
    brandId?: string;
}

const RetailMatchReportContainer = (props: IRetailMatchReportContainerProps): JSX.Element => {
    /** misc */
    const dispatch = useDispatch();

    const {
        isSharing,
        jobId,
    } = props;

    //If we are sharing, we have to handle brandId differently
    const match = useRouteMatch<IRetailMatchReportContainerShareRouteParams>(`/:brandId`);
    const brandId = isSharing
        ? match?.params?.brandId
        : props.brandId;

    /** store */
    const { jobResult, benchmarkRecord } = useSelector(retailMatchReportDataSelector);
    const loadingRetailMatchReport = useSelector(loadingRetailMatchReportSelector);
    const reportDataReady: boolean = useSelector(getRetailMatchReportDataReady);
    const userShareSettings: IUserShareSettings = useSelector(userShareSettingsSelector);
    const activeApp = useSelector(retailMatchActiveAppSelector);

    /** effects */
    useEffect(() => {
        /*
              Kicking this call off here because the Match report body needs it,
              yet it can take several seconds to complete, so I want it loading
              while we're having to wait on the rest of the report anyway
              */
        dispatch(getRetailMatchGeographies(RetailMatchGeographyOption.DMA));

        if (!userShareSettings) {
            dispatch(getUserShareSettings());
        }

        if (!isSharing) {
            if (activeApp !== ActiveApp.RETAIL_MATCHES) {
                dispatch(setActiveApp(ActiveApp.RETAIL_MATCHES));
            }
        }

        dispatch(getMosaicSegments());

        return () => {
            dispatch(clearRetailMatchResults());
        };
    }, []);

    useEffect(() => {
        if (!props.isSharing) {
            dispatch(setLoadingRetailMatchReport(true));
            dispatch(getResults(jobId));
        }
    }, [jobId]);

    const [isPrinting, setIsPrinting] = useState(false);
    const [renderType, setRenderType] = useState(RenderType.DEFAULT);
    const [isChoosingDigitalShare, setIsChoosingDigitalShare]
        = useState<boolean>(false);
    const [isDebuggingTemplate, setIsDebuggingTemplate] = useState(false);

    useEffect(() => {
        const brand = getBrandFromBrandId();
        dispatch(setSelectedBrand(brand));
    }, [brandId, jobResult]);

    useEffect(() => {
        const newBenchmark = jobResult?.chartData?.benchmarkRecords?.find(
            (benchmark) => benchmark.bid === brandId
        );
        if (newBenchmark) {
            dispatch(setRetailMatchBenchmarkRecord(newBenchmark));
        }
    }, [brandId]);

    const onChangeGenerating = (isGenerating: boolean) => {
        setRenderType(
            isGenerating ? RenderType.DIGITAL_SHARE : RenderType.DEFAULT
        );
    };

    const onPrintClick = () => {
        setIsPrinting(true);
    };

    const getBrandFromBrandId = () => {
        return (
            jobResult?.job?.brands?.find((brand) => {
                return brandId === brand.brandId;
            }) || jobResult?.job?.brands[0]
        );
    };

    const brand = getBrandFromBrandId();

    const showMulti = jobResult?.job?.brands?.length > 1 && !brandId;

    const digitalShareOutputs: IDigitalShareItem[] = showMulti
        ? retailMatchDigitalShareOutputs().filter(
            (o) => o.type === DigitalShareItemType.BrandComparison
        )
        : retailMatchDigitalShareOutputs().filter(
            (o) => o.type !== DigitalShareItemType.BrandComparison
        );

    (window as any).showTemplate = () => {
        setIsDebuggingTemplate(true);
    };
    (window as any).hideTemplate = () => {
        setIsDebuggingTemplate(false);
    };

    const fakeData: IBrandOverviewTableRecord[] = [];

    return (
        <>
            {
                reportDataReady && !loadingRetailMatchReport
                    ? <>
                        {isDebuggingTemplate && (
                            <RetailMatchDigitalShareTemplate
                                captionAboveTitle={"01"}
                                sectionTitle={DigitalShareItemType.PsychographicProfile}
                                disclaimer={GPSInsightsTemplateDisclaimer}
                                itemType={DigitalShareItemType.BrandComparison}
                                hideItemTypeTitle={true}
                                hideItemText={true}
                            >
                                <div>
                                    <RetailMatchIntroTextHeader
                                        address={"Address"}
                                        isSharing={true}
                                        multiBrand={true}
                                        sampleCount={null}
                                        brandName={null}
                                        brand={null}
                                        backDateVerbiage={"back date"}
                                        showEditAddressModal={null}
                                    />
                                    <StyledTable
                                        columns={[]}
                                        dataSource={fakeData}
                                        pagination={false}
                                    />
                                </div>
                            </RetailMatchDigitalShareTemplate>
                        )}
                        <ReportOuterWrapper
                            id="RetailMatchReportBody"
                            style={{
                                top: 0,
                                paddingTop: isSharing || !props.isScout ? 0 : topNavHeightPx
                            }}
                        >
                            {
                            /*
                                BJ 3/26/2021: I feel like this is hacky. We were getting lots of crazy JS errors around scroll animations.
                                This is because those scroll elements freak out when a child element is removed from the DOM,
                                and then re-rendered. To alleviate that, I had to keep both versions technically on the DOM,
                                but just set display: none of we wanted it gone. It looks like the errors are replaced with warnings,
                                Which at least doesn't flood our logs. If someone could make this cleaner, I will literally buy you a meal.
                                */
                            }
                            <div style={{ display: showMulti ? "inline" : "none" }}>
                                <RetailMatchMultiBrand
                                    jobResult={jobResult}
                                    benchmarkRecord={benchmarkRecord}
                                    isSharing={isSharing}
                                    renderType={renderType}
                                    onPrintClick={onPrintClick}
                                    onShareClick={() => setIsChoosingDigitalShare(true)}
                                />
                            </div>
                            <div style={{ display: showMulti ? "none" : "inline" }}>
                                <RetailMatchReport
                                    isSharing={isSharing}
                                    isScout={props.isScout}
                                    renderType={renderType}
                                    onPrintClick={onPrintClick}
                                    onShareClick={() => setIsChoosingDigitalShare(true)}
                                    brand={brand}
                                    jobResult={jobResult}
                                />
                            </div>
                        </ReportOuterWrapper>
                        {featureFlags.DigitalShare && isChoosingDigitalShare && (
                            <DigitalShareGenerator
                                onClose={() => setIsChoosingDigitalShare(false)}
                                onChangeGenerating={onChangeGenerating}
                                app={DigitalShareAppType.RetailMatch}
                                isChoosingDigitalShare={isChoosingDigitalShare}
                                outputs={digitalShareOutputs}
                            />
                        )}

                        {featureFlags.DigitalShare && isPrinting && (
                            <PrintGenerator
                                onComplete={() => setIsPrinting(false)}
                                onChangeGenerating={onChangeGenerating}
                                app={DigitalShareAppType.RetailMatch}
                                outputs={digitalShareOutputs}
                                jobId={jobId}
                            />
                        )}
                    </>
                    : <ComponentLoading title="Loading Match Analysis" />
            }
        </>
    );
};

export default RetailMatchReportContainer;
